import { useState } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
  SUBSCRIPTION_PLANS,
  getAvailableUpgradePlans,
  getPlanByRole,
  SubscriptionRole,
} from "../../utils/subscriptionPlans";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../reactvideoeditor/pro/components/ui/dialog";

interface SubscriptionUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SubscriptionUpgradeModal({
  isOpen,
  onClose,
}: SubscriptionUpgradeModalProps) {
  const user = useQuery(api.users.getCurrentUser);
  const createCheckout = useAction(api.payments.createCheckout);
  const [isUpgrading, setIsUpgrading] = useState<string | null>(null);

  const currentRole = (user?.profile?.role || "free") as SubscriptionRole;
  const currentPlan = getPlanByRole(currentRole);
  const availablePlans = getAvailableUpgradePlans(currentRole);

  const handleUpgrade = async (planType: "starter" | "pro") => {
    setIsUpgrading(planType);
    try {
      const result = await createCheckout({
        planType,
        returnUrl: window.location.origin + window.location.pathname,
      });

      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to initiate checkout");
      console.error(error);
      setIsUpgrading(null);
    }
  };

  const getPlanBadgeColor = (role: SubscriptionRole) => {
    switch (role) {
      case "pro":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "starter":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "free":
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white p-0">
        {/* Header */}
        <DialogHeader className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Upgrade Your Plan
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-1">
            Choose a plan that fits your needs
          </DialogDescription>
        </DialogHeader>

        {/* Current Plan Display */}
        {currentPlan && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">
                Current Plan:
              </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium border ${getPlanBadgeColor(currentRole)}`}
              >
                {currentPlan.name}
              </span>
              {user?.profile?.subscriptionStatus === "active" && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                  Active
                </span>
              )}
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="p-6">
          {availablePlans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">
                {currentRole === "pro"
                  ? "You're already on the highest plan!"
                  : "No upgrade plans available at this time."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {availablePlans.map((plan) => {
                const isProcessing = isUpgrading === plan.role;
                const canUpgrade = plan.role === "starter" || plan.role === "pro";

                return (
                  <div
                    key={plan.role}
                    className="border border-gray-200 rounded-lg p-6 hover:border-orange-500 transition-colors relative"
                  >
                    {/* Plan Header */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {plan.name}
                        </h3>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getPlanBadgeColor(plan.role)}`}
                        >
                          {plan.role.toUpperCase()}
                        </span>
                      </div>
                      <div className="mt-3">
                        <span className="text-3xl font-bold text-gray-900">
                          ${plan.price}
                        </span>
                        <span className="text-gray-600">/month</span>
                      </div>
                    </div>

                    {/* Features List */}
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, index) => (
                        <li
                          key={index}
                          className="flex items-start text-sm text-gray-600"
                        >
                          <svg
                            className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Upgrade Button */}
                    {canUpgrade ? (
                      <button
                        onClick={() => handleUpgrade(plan.role as "starter" | "pro")}
                        disabled={isProcessing || !!isUpgrading}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-md hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            Upgrade to {plan.name}
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 7l5 5m0 0l-5 5m5-5H6"
                              />
                            </svg>
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full px-4 py-2 text-sm font-medium text-gray-400 bg-gray-100 rounded-md cursor-not-allowed"
                      >
                        Coming Soon
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            All plans include a 30-day money-back guarantee. Cancel anytime.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

